import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CardComponent } from '../../../../shared/components/card/card.component';
import {
  InputWrapperComponent,
  isSubmitAllowed,
} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AsyncPipe, NgForOf, NgTemplateOutlet } from '@angular/common';
import {
  FieldConfigMap,
  FieldType,
  isSelectField,
} from '../../../../shared/components/input/interfaces/field-config-map';
import {
  ApiCisternResponseDto,
  DeviceType,
  ForecastPeriod,
  RepetitionMode,
  RuleConditionComparisonType,
  RuleConditionWeather,
  WeekDays,
} from '@birco/rainmesh-api';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { IconName } from '../../../../shared/icons/icon-map';
import { TextWithIconComponent } from '../../../../shared/components/text-with-icon/text-with-icon.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  catchError,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FormKeysPipe } from '../../../../shared/pipes/form-keys.pipe';
import { Store } from '@ngrx/store';
import { selectAllCisterns } from '../../../cisterns/state/cistern.selectors';
import { selectDevicesByType } from '../../../devices/state/device.selectors';
import { ActivatedRoute, Router } from '@angular/router';
import { selectRuleByIdAsEditForm } from '../../state/rule.selectors';
import { RuleActionFormModel } from '../../models/rule-action-form-model.interface';
import { RuleConditionFormModel } from '../../models/rule-condition-form-model.interface';
import { FormConditionType } from '../../enums/form-condition-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconsPipe } from '../../../../shared/pipes/icons.pipe';
import { LabelConverter } from '../../../../shared/converter/label.converter';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { CanComponentDeactivate } from '../../../../shared/guards/can-deactivate.guard';
import { DeviceActions } from '../../../devices/state/device.actions';
import { CisternActions } from '../../../cisterns/state/cistern.actions';
import { RuleActions } from '../../state/rule.actions';
import { clockTimeValidator } from '../../../../shared/components/input/validators/validators';
import { DeviceResponseDto } from '../../../devices/models/device-response.interface';
import { selectAllDeviceGroups } from '../../../devices/state/device-groups/device-group.selectors';
import { DeviceGroupActions } from '../../../devices/state/device-groups/device-group.actions';

@Component({
  selector: 'app-page-rule-form',
  imports: [
    CardComponent,
    InputWrapperComponent,
    ReactiveFormsModule,
    TranslatePipe,
    NgForOf,
    IconComponent,
    TextWithIconComponent,
    FormKeysPipe,
    NgTemplateOutlet,
    AsyncPipe,
    IconsPipe,
  ],
  templateUrl: './page-rule-form.component.html',
  styleUrl: './page-rule-form.component.scss',
})
export class PageRuleFormComponent implements OnInit, CanComponentDeactivate {
  private static readonly ACTION_TARGET_DEVICE = 'device';
  private static readonly ACTION_TARGET_DEVICE_GROUP = 'deviceGroup';

  private _fb = inject(FormBuilder);
  private translateService = inject(TranslateService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store);

  private destroyRef = inject(DestroyRef);

  rule$ =
    this.route.paramMap?.pipe(
      map((params) => params.get('id')),
      filter((id): id is string => !!id),
      tap((id) =>
        this.store.dispatch(
          RuleActions.public.loadRuleWithId({ id: id, skipIfInStore: true }),
        ),
      ),
      switchMap((id) => this.store.select(selectRuleByIdAsEditForm(id))),
    ) ?? of();

  ruleForm: FormGroup;

  conditionTypes: { label: string; value: FormConditionType }[] = [
    ...Object.entries(FormConditionType)
      .filter(([, value]) => value !== FormConditionType.weather) // currently not supported
      .map(([key, value]) => ({
        label: LabelConverter.getFormConditionTypeLabel(
          key as FormConditionType,
        ),
        value: value,
      })),
  ];

  fieldConfigs: FieldConfigMap = {
    cisternSensorDeviceId: {
      label: 'rules.detail.cistern',
      placeholder: 'default.placeholder.select',
      type: 'selectAsync',
      asyncOptions: this.store.select(selectAllCisterns).pipe(
        map((cisterns: ApiCisternResponseDto[]) => {
          return cisterns.map(
            (cistern) =>
              ({
                label: cistern.name,
                value: cistern.distanceSensor?.id,
              }) as { label: string; value: string },
          );
        }),
        startWith([]),
      ),
    },
    comparisonType: {
      label: 'rules.detail.comparisonType',
      placeholder: 'default.placeholder.select',
      type: 'select',
      options: [
        ...Object.entries(RuleConditionComparisonType).map(([key, value]) => ({
          label: LabelConverter.getRuleConditionComparisonTypeLabel(
            key as RuleConditionComparisonType,
          ),
          value: value,
        })),
      ],
    },
    threshold: {
      label: 'rules.detail.threshold',
      placeholder: 'default.placeholder.input',
      type: 'percent',
      unit: '%',
    },
    time: {
      label: 'rules.detail.time',
      placeholder: 'default.placeholder.input',
      type: 'time',
    },
    repetitionMode: {
      label: 'rules.detail.repetitionMode.label',
      placeholder: 'default.placeholder.select',
      type: 'select',
      options: [
        ...Object.entries(RepetitionMode).map(([key, value]) => ({
          label: LabelConverter.getRepetitionModeLabel(key as RepetitionMode),
          value: value,
        })),
      ],
    },
    // repetitionInterval: {
    //   label: "rules.detail.repetitionInterval.label",
    //   placeholder: "default.placeholder.input",
    //   type: 'number'
    // },
    repetitionDays: {
      label: 'rules.detail.repetitionDays.label',
      placeholder: 'default.placeholder.select',
      type: 'multiSelect',
      options: [
        ...[
          WeekDays.MONDAY,
          WeekDays.TUESDAY,
          WeekDays.WEDNESDAY,
          WeekDays.THURSDAY,
          WeekDays.FRIDAY,
          WeekDays.SATURDAY,
          WeekDays.SUNDAY,
        ].map((weekDay) => ({
          label: LabelConverter.getWeekDaysLabel(weekDay),
          value: weekDay,
        })),
      ],
    },
    sensorDeviceId: {
      label: 'rules.detail.sensorDevice',
      placeholder: 'default.placeholder.select',
      type: 'selectAsync',
      asyncOptions: this.store
        .select(selectDevicesByType(DeviceType.SOIL))
        .pipe(
          map((devices) =>
            this.filterAvailableDevices(devices).map((device) => ({
              label: device.name,
              value: device.id,
            })),
          ),
          startWith([]),
        ),
    },
    weather: {
      label: 'rules.detail.weather.label',
      placeholder: 'default.placeholder.select',
      type: 'select',
      options: [
        ...Object.entries(RuleConditionWeather).map(([key, value]) => ({
          label: LabelConverter.getWeatherLabel(key as RuleConditionWeather),
          value: value,
        })),
      ],
    },
    forecastPeriod: {
      label: 'rules.detail.forecastPeriod.label',
      placeholder: 'default.placeholder.select',
      type: 'select',
      options: [
        ...Object.entries(ForecastPeriod).map(([key, value]) => ({
          label: LabelConverter.getForecastPeriodLabel(key as ForecastPeriod),
          value: value,
        })),
      ],
    },
    deviceId: {
      label: 'rules.detail.device',
      placeholder: 'default.placeholder.select',
      type: 'selectAsync',
      asyncOptions: this.store
        .select(selectDevicesByType(DeviceType.VALVE))
        .pipe(
          map((devices) =>
            this.filterAvailableDevices(devices).map((device) => ({
              label: device.name,
              value: device.id,
            })),
          ),
          startWith([]),
        ),
    },
    deviceGroupId: {
      label: 'rules.detail.deviceGroup',
      placeholder: 'default.placeholder.select',
      type: 'selectAsync',
      asyncOptions: this.store.select(selectAllDeviceGroups).pipe(
        map((deviceGroups) =>
          deviceGroups.map((deviceGroup) => ({
            label: deviceGroup.name,
            value: deviceGroup.id,
          })),
        ),
        startWith([]),
      ),
    },
    duration: {
      label: 'rules.detail.duration',
      placeholder: 'default.placeholder.input',
      type: 'number',
      unit: 'min',
    },
  };

  hasSubmitted: boolean = false;

  constructor() {
    const _fb = this._fb;

    this.ruleForm = this._fb.group({
      id: [undefined],
      name: [undefined, Validators.required],
      conditions: _fb.array([], Validators.required),
      orConditions: _fb.array([]),
      actions: _fb.array([], Validators.required),
    });
  }

  getAsyncOptions(key: any): Observable<any> {
    return this.fieldConfigs[key].asyncOptions as Observable<any>;
  }

  ngOnInit() {
    this.ensureInitialDataLoaded();

    this.rule$
      .pipe(
        filter((rule) => !!rule),
        take(1),
        tap((rule) => {
          this.ruleForm.patchValue({
            id: rule.id,
            name: rule.name,
          });

          rule.conditions.map((condition) => {
            this.addCondition(condition.type, condition);
          });

          rule.orConditions.map((condition) => {
            this.addOrCondition(condition.type, condition);
          });

          rule.actions.map((action) => {
            const actionTarget = action.deviceGroupId
              ? PageRuleFormComponent.ACTION_TARGET_DEVICE_GROUP
              : PageRuleFormComponent.ACTION_TARGET_DEVICE;
            this.addAction(actionTarget, action);
          });
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError((_) => EMPTY),
      )
      .subscribe();
  }

  private ensureInitialDataLoaded() {
    this.store
      .select(selectAllCisterns)
      .pipe(take(1))
      .subscribe((cisterns) => {
        if (cisterns.length === 0) {
          this.store.dispatch(
            CisternActions.public.loadAllCisternsByFilters({}),
          );
        }
      });

    this.store
      .select(selectDevicesByType(DeviceType.SOIL))
      .pipe(take(1))
      .subscribe((soilDevices) => {
        this.store
          .select(selectDevicesByType(DeviceType.VALVE))
          .pipe(take(1))
          .subscribe((valveDevices) => {
            if (soilDevices.length === 0 || valveDevices.length === 0) {
              this.store.dispatch(
                DeviceActions.public.loadAllDevicesByFilters({
                  filter: {
                    deviceTypes: [DeviceType.SOIL, DeviceType.VALVE],
                    isOutOfService: false,
                  },
                }),
              );
            }
          });
      });

    this.store
      .select(selectAllDeviceGroups)
      .pipe(take(1))
      .subscribe((deviceGroups) => {
        if (deviceGroups.length === 0) {
          this.store.dispatch(
            DeviceGroupActions.public.loadAllDeviceGroupsByFilters({
              filter: { pageSize: 999 },
            }),
          );
        }
      });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.ruleForm.pristine || this.hasSubmitted) {
      return true;
    }

    const cancelData: ConfirmDialogData = {
      title: this.translateService.instant('rules.cancelForm.title'),
      body: this.translateService.instant('rules.cancelForm.body'),
      hint: this.translateService.instant('rules.cancelForm.hint'),
      confirmLabel: this.translateService.instant('rules.cancelForm.action'),
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: cancelData,
    });

    return dialogRef.afterClosed().pipe(map((result) => result === true));
  }

  get conditions() {
    return this.ruleForm.controls['conditions'] as FormArray;
  }

  get orConditions() {
    return this.ruleForm.controls['orConditions'] as FormArray;
  }

  get actions() {
    return this.ruleForm.controls['actions'] as FormArray;
  }

  keyTrackBy = (key: string | IconName) => key;

  private filterAvailableDevices(
    devices: DeviceResponseDto[],
  ): DeviceResponseDto[] {
    return devices.filter((device) => device.isOutOfService !== true);
  }

  canAddClockTime(conditions: FormArray) {
    return !conditions.controls.some(
      (c) => c.get('type')?.value === 'clockTime',
    );
  }

  addCondition(type: FormConditionType, condition?: RuleConditionFormModel) {
    if (
      type === FormConditionType.clockTime &&
      !this.canAddClockTime(this.conditions)
    )
      return;
    const conditionForm = this.generateConditionForm(type, condition);
    this.conditions.push(conditionForm);
  }

  removeCondition(index: number) {
    this.conditions.removeAt(index);
    this.ruleForm.markAsDirty();
  }

  addOrCondition(type: FormConditionType, condition?: RuleConditionFormModel) {
    if (
      type === FormConditionType.clockTime &&
      !this.canAddClockTime(this.orConditions)
    )
      return;
    const conditionForm = this.generateConditionForm(type, condition);
    this.orConditions.push(conditionForm);
  }

  removeOrCondition(index: number) {
    this.orConditions.removeAt(index);
    this.ruleForm.markAsDirty();
  }

  addAction(target: 'device' | 'deviceGroup', action?: RuleActionFormModel) {
    const actionForm = this._fb.group({
      id: [action?.id],
      target: [target, Validators.required],
      deviceId: [action?.deviceId],
      deviceGroupId: [action?.deviceGroupId],
      duration: [action?.duration, Validators.required],
    });

    const deviceIdControl = actionForm.get('deviceId');
    const deviceGroupIdControl = actionForm.get('deviceGroupId');

    if (target === PageRuleFormComponent.ACTION_TARGET_DEVICE) {
      deviceIdControl?.setValidators([Validators.required]);
      deviceIdControl?.enable({ emitEvent: false });
      deviceGroupIdControl?.setValue(undefined, { emitEvent: false });
      deviceGroupIdControl?.clearValidators();
      deviceGroupIdControl?.disable({ emitEvent: false });
    } else {
      deviceGroupIdControl?.setValidators([Validators.required]);
      deviceGroupIdControl?.enable({ emitEvent: false });
      deviceIdControl?.setValue(undefined, { emitEvent: false });
      deviceIdControl?.clearValidators();
      deviceIdControl?.disable({ emitEvent: false });
    }

    deviceIdControl?.updateValueAndValidity({ emitEvent: false });
    deviceGroupIdControl?.updateValueAndValidity({ emitEvent: false });

    this.actions.push(actionForm);
  }

  removeAction(index: number) {
    this.actions.removeAt(index);
    this.ruleForm.markAsDirty();
  }

  getConditionType(type: FormConditionType): any {
    return this.conditionTypes.find((c) => c.value === type);
  }

  private generateConditionForm(
    type: FormConditionType,
    condition?: RuleConditionFormModel,
  ): FormGroup {
    let conditionForm: FormGroup;
    switch (type) {
      case FormConditionType.cistern:
        conditionForm = this._fb.group({
          id: [condition?.id],
          type: ['cistern', Validators.required],
          cisternSensorDeviceId: [
            condition?.cisternSensorDeviceId,
            Validators.required,
          ],
          comparisonType: [condition?.comparisonType, Validators.required],
          threshold: [condition?.threshold, Validators.required],
        });
        break;
      case FormConditionType.clockTime: {
        const weekdayString = new Date()
          .toLocaleDateString('en-US', { weekday: 'long' })
          .toUpperCase();
        const currentWeekday = WeekDays[weekdayString as keyof typeof WeekDays];

        conditionForm = this._fb.group(
          {
            id: [condition?.id],
            type: ['clockTime', Validators.required],
            time: [condition?.time, Validators.required],
            repetitionMode: [condition?.repetitionMode, Validators.required],
            // repetitionInterval: [condition?.repetitionInterval ?? 1, Validators.min(1)],
            repetitionDays: this._fb.group(
              Object.fromEntries(
                Object.keys(WeekDays).map((day) => [
                  day,
                  new FormControl(
                    condition?.repetitionDays
                      ? (condition.repetitionDays[day as WeekDays] ?? false)
                      : day === currentWeekday,
                  ),
                ]),
              ),
            ),
          },
          { validators: clockTimeValidator },
        );

        conditionForm
          .get('repetitionMode')
          ?.valueChanges.pipe(
            startWith(conditionForm.get('repetitionMode')?.value),
            takeUntilDestroyed(this.destroyRef),
            catchError((_) => EMPTY),
          )
          .subscribe((repetitionMode) => {
            // const repetitionInterval = conditionForm.get('repetitionInterval');
            const repetitionDays = conditionForm.get('repetitionDays');

            if (repetitionMode === RepetitionMode.WEEKLY) {
              // repetitionInterval?.enable();
              repetitionDays?.enable();
            } else if (repetitionMode === RepetitionMode.DAILY) {
              // repetitionInterval?.enable();
              repetitionDays?.disable();
            } else {
              // repetitionInterval?.disable();
              repetitionDays?.disable();
            }
          });
        break;
      }
      case FormConditionType.soil:
        conditionForm = this._fb.group({
          id: [condition?.id],
          type: ['soil', Validators.required],
          sensorDeviceId: [condition?.sensorDeviceId, Validators.required],
          comparisonType: [condition?.comparisonType, Validators.required],
          threshold: [condition?.threshold, Validators.required],
        });
        break;
      case FormConditionType.weather:
        conditionForm = this._fb.group({
          id: [condition?.id],
          type: ['weather', Validators.required],
          weather: [condition?.weather, Validators.required],
          forecastPeriod: [condition?.forecastPeriod, Validators.required],
        });
    }
    return conditionForm;
  }

  onSubmit() {
    if (!isSubmitAllowed(this.ruleForm)) {
      return;
    }

    if (!this.ruleForm.pristine) {
      this.hasSubmitted = true;
      this.store.dispatch(
        RuleActions.public.createOrUpdateRule({
          id: this.ruleForm.value.id,
          requestDto: this.ruleForm.value,
        }),
      );
    } else {
      this.navigateBack();
    }
  }

  onCancel(): void {
    this.navigateBack();
  }

  navigateBack() {
    const ruleId = this.ruleForm.get('id')?.value;
    if (ruleId) {
      this.router.navigate([RouteConstants.routeRule, ruleId]);
    } else {
      this.router.navigate([RouteConstants.routeRule]);
    }
  }

  isSelectField(type: FieldType): boolean {
    return isSelectField(type);
  }

  protected readonly FormConditionType = FormConditionType;
}
